import type {
  CandidateStatus,
  CandidateView,
  ProbeSide,
  ReportCert,
} from '../api/passport-cert-list';

export type CandidateAction = 'approve' | 'reject' | 'rollback';

export function candidateActions(c: CandidateView): CandidateAction[] {
  switch (c.status) {
    case 'pending':
      return ['approve', 'reject'];
    case 'rolled_back':
      return ['approve'];
    case 'approved':
      return c.isLive ? ['rollback'] : [];
    default:
      return [];
  }
}

export function candidateHeadline(c: CandidateView): string {
  const r = c.report;
  const certDelta = r ? ` (+${r.added.length} / −${r.removed.length})` : '';
  const countryDelta = r ? ` (${signed(r.after.countries - r.before.countries)})` : '';
  return `${c.version} · ${c.certificateCount} certificates${certDelta} · ${c.countryCount} countries${countryDelta}`;
}

function signed(n: number): string {
  return n < 0 ? `−${Math.abs(n)}` : `+${n}`;
}

const STATUS_LABELS: Record<CandidateStatus, string> = {
  pending: 'Waiting for your approval',
  approved: 'Approved',
  rolled_back: 'Rolled back',
  rejected: 'Rejected',
  superseded: 'Replaced by a newer list',
  blocked: 'Blocked by a safety check',
  already_live: 'Already live',
};

export function statusLabel(s: CandidateStatus): string {
  return STATUS_LABELS[s];
}

export interface CountryGroup {
  country: string;
  name: string;
  count: number;
  certs: ReportCert[];
}

export function countryGroups(certs: ReportCert[]): CountryGroup[] {
  const byCode = new Map<string, CountryGroup>();
  for (const c of certs) {
    const g = byCode.get(c.country);
    if (g) {
      g.count += 1;
      g.certs.push(c);
    } else {
      byCode.set(c.country, { country: c.country, name: c.countryName, count: 1, certs: [c] });
    }
  }
  return [...byCode.values()].sort((a, b) => a.country.localeCompare(b.country));
}

export function certLine(c: ReportCert): string {
  return `${c.subjectCN ?? '(no CN)'} · valid to ${c.validTo.slice(0, 10)}`;
}

/** A removed certificate that has not expired yet is the alarming case: passports it signed stop verifying. */
export function certStillValid(validTo: string, now: Date): boolean {
  const t = Date.parse(validTo);
  return Number.isFinite(t) && t > now.getTime();
}

export function probeLabel(name: string, side: ProbeSide): string {
  return `${name}: ${side.ok ? 'OK' : 'failed'}`;
}

export function safeGithubUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && u.hostname === 'github.com' ? u.href : null;
  } catch {
    return null;
  }
}
